import { flowApi } from '.';
import { TAG_TYPES } from 'types/services';
import type {
  GetActionValueSourcesReq,
  GetActionValueSourcesRes,
  GetActionsTargetsReq,
  GetActionsTargetsRes,
  GetColumnsToShowReq,
  GetColumnsToShowRes,
  GetComputedJoinTypesReq,
  GetComputedJoinTypesRes,
  GetComputedSourcesReq,
  GetComputedSourcesRes,
  GetConditionComparisonsReq,
  GetConditionComparisonsRes,
  GetConditionSourcesReq,
  GetConditionSourcesRes,
  GetConditionValueSourcesReq,
  GetConditionValueSourcesRes,
  GetDateSourcesReq,
  GetDateSourcesRes,
  GetDeleteNotificationFormSectionsReq,
  GetDeleteNotificationFormSectionsRes,
  GetFilterLinkedFormSourcesReq,
  GetFilterLinkedTableFormSourcesReq,
  GetFormItemsToShowReq,
  GetFormItemsToShowRes,
  GetNotifyActionValueSourcesReq,
  GetNotifyActionValueSourcesRes,
  GetNotifyAddresseeAttributeSourcesReq,
  GetNotifyAddresseeAttributeSourcesRes,
  GetNotifyAddresseesReq,
  GetNotifyAddresseesRes,
  GetOtherRecordsReq,
  GetOtherRecordsRes,
  GetViewFilterFormSourcesReq,
  GetViewFilterFormSourcesRes,
  GetViewFilterTableFormSourcesRes,
  GetViewFilterTableSourcesReq,
  GetViewFilterTableSourcesRes,
} from 'types';

export const agendaLookupsService = flowApi.injectEndpoints({
  overrideExisting: true,
  endpoints: (builder) => ({
    getActionsTargets: builder.query<GetActionsTargetsRes, GetActionsTargetsReq>({
      query: ({ agendaId, otherRecordAgendaId, otherRecordFormItemId }) => ({
        url: `/Agendas/${agendaId}/Lookups/ActionTargets`,
        method: 'GET',
        params: {
          otherRecordAgendaId,
          otherRecordFormItemId,
        },
      }),
      providesTags: [TAG_TYPES.LOOKUPS],
    }),
    getActionValueSources: builder.query<GetActionValueSourcesRes, GetActionValueSourcesReq>({
      query: ({ agendaId, targetFormItemId, actionType }) => ({
        url: `/Agendas/${agendaId}/Lookups/ActionValueSources`,
        method: 'GET',
        params: {
          targetFormItemId,
          actionType,
        },
      }),
      providesTags: [TAG_TYPES.LOOKUPS],
    }),
    getForEachActionValueSources: builder.query<GetActionValueSourcesRes, GetActionValueSourcesReq>({
      query: ({ agendaId, targetFormItemId }) => ({
        url: `/Agendas/${agendaId}/Lookups/ForEachSources`,
        method: 'GET',
        params: {
          targetFormItemId,
        },
      }),
      providesTags: [TAG_TYPES.LOOKUPS],
    }),
    getColumnsToShow: builder.query<GetColumnsToShowRes, GetColumnsToShowReq>({
      query: ({ agendaId, tableViewId }) => ({
        url: `/Agendas/${agendaId}/Lookups/ColumnsToShow`,
        method: 'GET',
        params: {
          tableViewId,
        },
      }),
      providesTags: [TAG_TYPES.LOOKUPS],
    }),
    getComputedJoinTypes: builder.query<GetComputedJoinTypesRes, GetComputedJoinTypesReq>({
      query: () => ({
        url: '/AgendaLookups/ComputedJoinTypes/Me',
        method: 'GET',
      }),
      providesTags: [TAG_TYPES.LOOKUPS],
    }),
    getComputedSources: builder.query<GetComputedSourcesRes, GetComputedSourcesReq>({
      query: ({ agendaId, currentFormItemId }) => ({
        url: `/Agendas/${agendaId}/Lookups/ComputedSources`,
        method: 'GET',
        params: {
          currentFormItemId,
        },
      }),
      providesTags: [TAG_TYPES.LOOKUPS],
    }),
    getConditionComparisons: builder.query<GetConditionComparisonsRes, GetConditionComparisonsReq>({
      query: ({ agendaId, sourceFormItemId, sourceType }) => ({
        url: `/Agendas/${agendaId}/Lookups/ConditionComparisons`,
        method: 'GET',
        params: {
          sourceFormItemId,
          sourceType,
        },
      }),
      providesTags: [TAG_TYPES.LOOKUPS],
    }),
    getConditionSources: builder.query<GetConditionSourcesRes, GetConditionSourcesReq>({
      query: ({ agendaId }) => ({
        url: `/Agendas/${agendaId}/Lookups/ConditionSources`,
        method: 'GET',
      }),
      providesTags: [TAG_TYPES.LOOKUPS],
    }),
    getConditionValueSources: builder.query<GetConditionValueSourcesRes, GetConditionValueSourcesReq>({
      query: ({ agendaId, sourceFormItemId, sourceType }) => ({
        url: `/Agendas/${agendaId}/Lookups/ConditionValueSources`,
        method: 'GET',
        params: {
          sourceFormItemId,
          sourceType,
        },
      }),
      providesTags: [TAG_TYPES.LOOKUPS],
    }),
    getDateSources: builder.query<GetDateSourcesRes, GetDateSourcesReq>({
      query: ({ agendaId, currentFormItemId }) => ({
        url: `/Agendas/${agendaId}/Lookups/DateSources`,
        method: 'GET',
        params: {
          currentFormItemId,
        },
      }),
      providesTags: [TAG_TYPES.LOOKUPS],
    }),
    getDeleteNotificationFormSections: builder.query<GetDeleteNotificationFormSectionsRes, GetDeleteNotificationFormSectionsReq>({
      query: ({ agendaId, otherRecordAgendaId, otherRecordFormItemId }) => ({
        url: `/Agendas/${agendaId}/Lookups/DeleteNotificationFormSections`,
        method: 'GET',
        params: {
          otherRecordAgendaId,
          otherRecordFormItemId,
        },
      }),
      providesTags: [TAG_TYPES.LOOKUPS],
    }),
    getFormItemsToShow: builder.query<GetFormItemsToShowRes, GetFormItemsToShowReq>({
      query: ({ agendaId, viewId }) => ({
        url: `/Agendas/${agendaId}/Lookups/FormItemsToShow`,
        method: 'GET',
        params: {
          viewId,
        },
      }),
      providesTags: [TAG_TYPES.LOOKUPS],
    }),
    getNotifyActionValueSources: builder.query<GetNotifyActionValueSourcesRes, GetNotifyActionValueSourcesReq>({
      query: ({ agendaId, otherRecordAgendaId, otherRecordFormItemId }) => ({
        url: `/Agendas/${agendaId}/Lookups/NotifyActionValueSources`,
        method: 'GET',
        params: {
          otherRecordAgendaId,
          otherRecordFormItemId,
        },
      }),
      providesTags: [TAG_TYPES.LOOKUPS],
    }),
    getNotifyAddressees: builder.query<GetNotifyAddresseesRes, GetNotifyAddresseesReq>({
      query: ({ agendaId, otherRecordAgendaId, otherRecordFormItemId }) => ({
        url: `/Agendas/${agendaId}/Lookups/NotifyAddressees`,
        method: 'GET',
        params: {
          otherRecordAgendaId,
          otherRecordFormItemId,
        },
      }),
      providesTags: [TAG_TYPES.LOOKUPS],
    }),
    getNotifyAddresseeAttributeSources: builder.query<GetNotifyAddresseeAttributeSourcesRes, GetNotifyAddresseeAttributeSourcesReq>({
      query: ({ agendaId, addresseeFormItemId }) => ({
        url: `/Agendas/${agendaId}/Lookups/NotifyAddresseeAttributeSources`,
        method: 'GET',
        params: {
          addresseeFormItemId,
        },
      }),
      providesTags: [TAG_TYPES.LOOKUPS],
    }),
    getOtherRecords: builder.query<GetOtherRecordsRes, GetOtherRecordsReq>({
      query: ({ agendaId }) => ({
        url: `/Agendas/${agendaId}/Lookups/OtherRecords`,
        method: 'GET',
      }),
    }),
    getViewFilterFormSources: builder.query<GetViewFilterFormSourcesRes, GetViewFilterFormSourcesReq>({
      query: ({ agendaId, viewFormItemId, viewId }) => ({
        url: `/Agendas/${agendaId}/Lookups/ViewFilterFormSources`,
        method: 'GET',
        params: {
          viewFormItemId,
          viewId,
        },
      }),
      providesTags: [TAG_TYPES.LOOKUPS],
    }),
    getFilterLinkedFormSources: builder.query<GetViewFilterFormSourcesRes, GetFilterLinkedFormSourcesReq>({
      query: ({ agendaId, viewFormItemId, viewId, viewAgendaId }) => ({
        url: `/Agendas/${agendaId}/Lookups/ViewFilterLinkedFormSources`,
        method: 'GET',
        params: {
          viewFormItemId,
          viewId,
          viewAgendaId,
        },
      }),
      providesTags: [TAG_TYPES.LOOKUPS],
    }),
    getFilterLinkedTableFormSources: builder.query<GetViewFilterTableFormSourcesRes, GetFilterLinkedTableFormSourcesReq>({
      query: ({ agendaId, columnId, viewId, tableId }) => ({
        url: `/Agendas/${agendaId}/Lookups/ViewFilterLinkedTableFormSources`,
        method: 'GET',
        params: {
          columnId,
          viewId,
          tableId,
        },
      }),
      providesTags: [TAG_TYPES.LOOKUPS],
    }),
    getViewFilterTableSources: builder.query<GetViewFilterTableSourcesRes, GetViewFilterTableSourcesReq>({
      query: ({ agendaId, viewId, columnId }) => ({
        url: `/Agendas/${agendaId}/Lookups/ViewFilterTableSources`,
        method: 'GET',
        params: {
          viewId,
          columnId,
        },
      }),
      providesTags: [TAG_TYPES.LOOKUPS],
    }),
  }),
});
export const {
  useGetActionsTargetsQuery,
  useLazyGetActionsTargetsQuery,
  useGetActionValueSourcesQuery,
  useLazyGetActionValueSourcesQuery,
  useGetForEachActionValueSourcesQuery,
  useGetColumnsToShowQuery,
  useGetComputedJoinTypesQuery,
  useGetComputedSourcesQuery,
  useGetConditionComparisonsQuery,
  useGetConditionSourcesQuery,
  useGetConditionValueSourcesQuery,
  useGetDateSourcesQuery,
  useGetDeleteNotificationFormSectionsQuery,
  useGetFilterLinkedFormSourcesQuery,
  useGetFormItemsToShowQuery,
  useGetNotifyActionValueSourcesQuery,
  useGetNotifyAddresseeAttributeSourcesQuery,
  useGetNotifyAddresseesQuery,
  useGetOtherRecordsQuery,
  useGetViewFilterFormSourcesQuery,
  useGetViewFilterTableSourcesQuery,
  useLazyGetColumnsToShowQuery,
  useLazyGetComputedSourcesQuery,
  useLazyGetConditionComparisonsQuery,
  useLazyGetConditionSourcesQuery,
  useLazyGetConditionValueSourcesQuery,
  useLazyGetDateSourcesQuery,
  useLazyGetDeleteNotificationFormSectionsQuery,
  useLazyGetFormItemsToShowQuery,
  useLazyGetNotifyActionValueSourcesQuery,
  useLazyGetNotifyAddresseeAttributeSourcesQuery,
  useLazyGetNotifyAddresseesQuery,
  useLazyGetOtherRecordsQuery,
  useLazyGetViewFilterFormSourcesQuery,
  useLazyGetViewFilterTableSourcesQuery,
  useLazyGetFilterLinkedFormSourcesQuery,
  useLazyGetFilterLinkedTableFormSourcesQuery,
} = agendaLookupsService;
